// #5: Restaurant Activity Dashboard — read-only aggregation over existing
// orders + order ledger. Shows totals, collection-per-user, by-type and
// by-day breakdowns. No business logic; safe to open anytime.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Chip,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const Cur = company?.currency || '₹';
const money = (n) => `${Cur}${Number(n || 0).toLocaleString('en-IN')}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

// Simple horizontal bar (no chart lib) — keeps the page dependency-free.
function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <Box sx={{ bgcolor: '#eef2f7', borderRadius: 1, height: 10, overflow: 'hidden' }}>
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, transition: 'width .3s' }} />
    </Box>
  );
}

const STAT = [
  ['totalOrders', 'Total Orders', '#1d4ed8'],
  ['revenue', 'Revenue', '#059669'],
  ['gst', 'GST', '#7c3aed'],
  ['discount', 'Discount', '#d97706'],
  ['cancelled', 'Cancelled', '#dc2626'],
  ['settled', 'Settled', '#0891b2'],
];

export default function RestaurantActivity() {
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!company?.id) return;
    setLoading(true);
    fetch(
      `${config.API_BASE_URL}/restaurantorder/activity/${company.id}?from=${from}&to=${to}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((r) => r.json())
      .then((d) => setData(d || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = data?.summary || {};
  const byUser = data?.byUser || [];
  const byType = data?.byType || [];
  const byDay = data?.byDay || [];
  const maxColl = useMemo(
    () => Math.max(1, ...byUser.map((u) => Number(u.collection || 0))),
    [byUser],
  );
  const maxDay = useMemo(
    () => Math.max(1, ...byDay.map((d) => Number(d.revenue || 0))),
    [byDay],
  );

  const exportCsv = () => {
    const rows = [
      ['User', 'Collection', 'Payments'],
      ...byUser.map((u) => [u.userName, u.collection, u.payments]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `RestaurantActivity_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f6fb', minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: '#fff',
          background:
            'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#1e3a8a 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}>
        <GroupsIcon sx={{ fontSize: 34 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant='h5' fontWeight={800} sx={{ color: '#fff' }}>
            Restaurant Activity
          </Typography>
          <Typography sx={{ opacity: 0.85, color: '#fff' }}>
            Orders, collection & performance — {from} to {to}
          </Typography>
        </Box>
        <TextField
          type='date'
          size='small'
          label='From'
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          sx={{ bgcolor: '#fff', borderRadius: 1 }}
        />
        <TextField
          type='date'
          size='small'
          label='To'
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          sx={{ bgcolor: '#fff', borderRadius: 1 }}
        />
        <Button variant='contained' color='inherit' startIcon={<RefreshIcon />} onClick={load}
          sx={{ color: '#0a2540', fontWeight: 700 }}>
          Apply
        </Button>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stat cards */}
          <Grid container spacing={2} mb={3}>
            {STAT.map(([key, label, color]) => (
              <Grid item xs={6} md={2} key={key}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e8eef7' }}>
                  <Typography variant='caption' color='text.secondary'>
                    {label}
                  </Typography>
                  <Typography fontWeight={800} sx={{ color, fontSize: 20 }}>
                    {['revenue', 'gst', 'discount'].includes(key)
                      ? money(s[key])
                      : s[key] ?? 0}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            {/* Collection per user */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e8eef7' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Typography fontWeight={800}>Collection per User</Typography>
                  <Button size='small' startIcon={<FileDownloadIcon />} onClick={exportCsv} sx={{ ml: 'auto' }}>
                    Export
                  </Button>
                </Box>
                {byUser.length === 0 ? (
                  <Typography color='text.secondary'>No collections in this range.</Typography>
                ) : (
                  byUser.map((u, i) => (
                    <Box key={i} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant='body2' fontWeight={600}>
                          {u.userName} <span style={{ color: '#94a3b8' }}>({u.payments})</span>
                        </Typography>
                        <Typography variant='body2' fontWeight={700}>
                          {money(u.collection)}
                        </Typography>
                      </Box>
                      <Bar value={Number(u.collection)} max={maxColl} color='#1d4ed8' />
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>

            {/* Orders by type */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e8eef7' }}>
                <Typography fontWeight={800} sx={{ mb: 1.5 }}>
                  Orders by Type
                </Typography>
                {byType.length === 0 ? (
                  <Typography color='text.secondary'>No orders.</Typography>
                ) : (
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>Orders</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byType.map((t, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Chip size='small' label={t.type} sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell align='right'>{t.count}</TableCell>
                          <TableCell align='right'>{money(t.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>

            {/* Orders by day */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e8eef7' }}>
                <Typography fontWeight={800} sx={{ mb: 1.5 }}>
                  Revenue by Day
                </Typography>
                {byDay.length === 0 ? (
                  <Typography color='text.secondary'>No orders in this range.</Typography>
                ) : (
                  byDay.map((d, i) => (
                    <Box key={i} sx={{ mb: 1.2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant='body2'>
                          {new Date(d.date).toLocaleDateString('en-IN')} · {d.orders} orders
                        </Typography>
                        <Typography variant='body2' fontWeight={700}>
                          {money(d.revenue)}
                        </Typography>
                      </Box>
                      <Bar value={Number(d.revenue)} max={maxDay} color='#059669' />
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
